import { useEffect, useRef, KeyboardEventHandler, ClipboardEventHandler, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { Box, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import CustomButton from 'components/Common/Button';
import { resendVerificationEmail, verifyEmailPin } from 'redux/slices/auth/authSlice';
import { notify } from 'components/Common/Toast';
import { AppDispatch } from 'redux/store';
import Routes from 'constants/routes';

type ValidateCodeInputs = {
    code1: string;
    code2: string;
    code3: string;
    code4: string;
    code5: string;
    code6: string;
};

const VerifyEmailPin = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const email = (location.state as { email?: string } | null)?.email;
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!email) {
            navigate(Routes.auth.login);
        }
    }, [email, navigate]);

    const focus1 = useRef<HTMLInputElement>(null);
    const focus2 = useRef<HTMLInputElement>(null);
    const focus3 = useRef<HTMLInputElement>(null);
    const focus4 = useRef<HTMLInputElement>(null);
    const focus5 = useRef<HTMLInputElement>(null);
    const focus6 = useRef<HTMLInputElement>(null);

    const focusRefs = [focus1, focus2, focus3, focus4, focus5, focus6];
    const [error, setError] = useState('');
    const { control, handleSubmit, getValues, setValue, watch } = useForm<ValidateCodeInputs>({
        defaultValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
    });
    const allFilled = Object.values(watch(['code1', 'code2', 'code3', 'code4', 'code5', 'code6'])).every((value) => value);

    const validateCode = async (data: ValidateCodeInputs) => {
        const code = Object.values(data).join('');
        if (code.length < 6) {
            setError('Please fill all the fields.');
            return;
        }
        setError('');
        setIsLoading(true);
        try {
            const res: any = await dispatch(verifyEmailPin({ email: email as string, pin: code }));
            if (res?.error) return;
            notify('Email verified! Please sign in.', 'success');
            navigate(Routes.auth.login);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (!email) return;
        const res: any = await dispatch(resendVerificationEmail({ email }));
        if (res?.error) return;
        notify('A new verification code has been sent to your email address.', 'success');
    };

    const handleBackspace: KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (e.key === 'Backspace') {
            const currentRef = document.activeElement as HTMLInputElement;
            const currentIndex = focusRefs.findIndex((ref) => ref?.current === currentRef);
            if (currentIndex > 0 && currentRef.value === '') {
                const prevIndex = currentIndex - 1;
                const prevRef = focusRefs[prevIndex]?.current;
                if (prevRef) {
                    prevRef.focus();
                    setValue(`code${prevIndex + 1}` as keyof ValidateCodeInputs, '');
                }
            }
        }
    };

    // Each box is maxLength=1, so a native paste only ever lands one character in the focused
    // box and drops the rest — this fans the clipboard's digits out across the remaining boxes.
    const handlePaste: ClipboardEventHandler<HTMLDivElement> = (e) => {
        const digits = e.clipboardData
            .getData('text')
            .replace(/[^0-9]/g, '')
            .split('');
        if (!digits.length) return;
        e.preventDefault();
        const currentRef = document.activeElement as HTMLInputElement;
        const startIndex = Math.max(
            0,
            focusRefs.findIndex((ref) => ref?.current === currentRef)
        );
        digits.slice(0, 6 - startIndex).forEach((digit, i) => {
            const fieldName = `code${startIndex + i + 1}` as keyof ValidateCodeInputs;
            setValue(fieldName, digit);
        });
        const lastFilledIndex = Math.min(startIndex + digits.length, 6) - 1;
        focusRefs[lastFilledIndex]?.current?.focus();
        if (Object.values(getValues()).every((value) => value)) {
            setError('');
        }
    };

    const handleChange = (text: string, index: number) => {
        if (text && index < 5) {
            focusRefs[index + 1]?.current?.focus();
        }
        const values = getValues();
        const allFilled = Object.values(values).every((value) => value);
        if (allFilled) {
            setError('');
        }
    };

    return (
        <AuthLayout>
            <div className="Auth-form">
                <Box p={5}>
                    <Typography variant="h4">Verify your email</Typography>
                    <br />
                    <Typography>
                        We have sent a 6-digit verification code to {email || 'your email address'}. Enter it below to verify your account.
                    </Typography>
                    <Stack direction="column" alignItems="center">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                margin: '20px 0',
                            }}
                        >
                            <Controller
                                name="code1"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus1}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 0);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                            <Controller
                                name="code2"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus2}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 1);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                            <Controller
                                name="code3"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus3}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 2);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                            <Controller
                                name="code4"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus4}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 3);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                            <Controller
                                name="code5"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus5}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 4);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                            <Controller
                                name="code6"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus6}
                                        onChange={(e) => {
                                            const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
                                            onChange(text);
                                            handleChange(text, 5);
                                        }}
                                        onKeyDown={handleBackspace}
                                        onPaste={handlePaste}
                                    />
                                )}
                            />
                        </div>
                        {error && (
                            <Box>
                                <Typography variant="body1" sx={{ color: 'red' }}>
                                    {error}
                                </Typography>
                            </Box>
                        )}
                        <Stack alignItems="center" direction="row">
                            <Typography>Didn’t receive a code?</Typography>
                            <CustomButton
                                onClick={handleResendCode}
                                sx={{
                                    '&:hover': { background: 'none', boxShadow: 'none' },
                                    color: 'black',
                                    background: 'none',
                                    fontSize: 16,
                                    textDecoration: 'underline',
                                }}
                            >
                                Resend Code
                            </CustomButton>
                        </Stack>
                    </Stack>
                    <Box sx={{ textAlign: 'center', mt: 10 }}>
                        <CustomButton width={'100%'} loading={isLoading} disabled={!allFilled} onClick={handleSubmit(validateCode)}>
                            Continue
                        </CustomButton>
                    </Box>
                </Box>
            </div>
        </AuthLayout>
    );
};

export default VerifyEmailPin;
